/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   lakehouseDetailHelp.ts
 *     Description     :   Help content data for the Lakehouse Detail Page
 *     Author          :   Peter Dobler
 *     Created         :   2026-02-16
 *     Modified        :   2026-02-16 | Peter Dobler | Initial implementation
 *     Constitution    :   v1.12.0 (Enterprise Dependency Architecture)
 *     Feature         :   135-ui-data-alignment
 * =========================================================================================
 *
 *  Structured help content consumed by <PageHelpPanel> on the Lakehouse
 *  Detail Page. Covers tabs, retention, excluded tables, storage mapping,
 *  backup status, and capacity type.
 *
 *  @module modules/backup/help
 *  @spec 135-ui-data-alignment
 *  @story US6 - Self-Service Help
 */

import type { PageHelpContent } from '../../../components/ui/PageHelpPanel';

// =============================================================================
// TOOLTIP STRINGS
// =============================================================================

export const lakehouseFieldTooltips = {
  backupStatus:
    'Whether automated backups are enabled for this lakehouse. When enabled, backups run on the configured schedule.',
  retentionDays:
    'Number of days backup snapshots are retained before automatic cleanup. Older snapshots are deleted to manage storage costs.',
  excludedTables:
    'Comma-separated list of table names excluded from backups. Use this to skip large staging tables or temporary data.',
  capacityType:
    'The Fabric capacity SKU type (F, P, EM, A) that hosts this lakehouse. Determines performance and billing.',
  storageMapping:
    'Azure Storage Account linked to this lakehouse for backup storage. Each lakehouse requires exactly one storage mapping.',
  fabricId:
    'Unique identifier assigned by Microsoft Fabric. Used internally for API calls and cross-referencing.',
  tabs: {
    overview: 'Basic lakehouse information including name, workspace, backup status, and capacity details.',
    storage: 'Manage Azure Storage Account mappings for backup destinations.',
    backups: 'View history of past backup runs, statuses, and durations.',
    config: 'Configure backup schedule, retention policy, and table exclusions.',
  },
} as const;

// =============================================================================
// HELP PANEL CONTENT
// =============================================================================

export const lakehouseDetailHelp: PageHelpContent = {
  pageTitle: 'Lakehouse Detail',
  purpose:
    'View and manage a single Fabric Lakehouse — backup configuration, storage mappings, and backup history.',
  sections: [
    {
      title: 'Key Concepts',
      concepts: [
        {
          term: 'Backup Enabled',
          definition:
            'When enabled, the system will run automated backups on the configured schedule. Disabling pauses all future backup runs without deleting existing snapshots.',
        },
        {
          term: 'Retention Days',
          definition:
            'How long backup snapshots are kept. After this many days, old snapshots are automatically deleted during the cleanup job.',
        },
        {
          term: 'Excluded Tables',
          definition:
            'Tables listed here are skipped during backup. Useful for large staging/temp tables that do not need recovery protection.',
        },
        {
          term: 'Storage Mapping',
          definition:
            'Links this lakehouse to an Azure Storage Account where backups are stored. Must be configured before the first backup can run.',
        },
        {
          term: 'Onboarding Progress',
          definition:
            'The workflow banner at the top shows which onboarding steps are complete and what action is needed next.',
        },
      ],
    },
    {
      title: 'Tab Guide',
      paragraphs: [
        'Overview — Name, workspace, backup status, schedule details, and capacity information.',
        'Storage Accounts — Add or remove Azure Storage Account mappings. A mapping is required before backups can run.',
        'Backup History — Chronological list of past backup runs with status (Completed, Failed, Running), duration, and size.',
        'Configuration — Edit backup schedule (frequency, time, days), retention days, and excluded tables.',
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Follow the onboarding banner to complete any remaining setup steps.' },
        { step: 2, instruction: 'Go to the Storage tab to add a storage account mapping if one is missing.' },
        { step: 3, instruction: 'Go to Configuration to enable backups and set a schedule.' },
        { step: 4, instruction: 'Check Backup History to verify that runs are completing successfully.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Lakehouses List', href: '/lakehouses' },
        { label: 'Workflow Dashboard', href: '/workflows' },
        { label: 'Data Collection Settings', href: '/settings/data-collection' },
      ],
      defaultCollapsed: true,
    },
  ],
};
